import { upload, get, post } from '@/utils/request'
import { showToast } from 'vant'
import { t } from '@/locales'

/**
 * 用户相关 API
 */

/**
 * @typedef {Object} Member - 用户完整信息
 * @property {string} id - 加密ID
 * @property {string} member_id - 未加密ID
 * @property {string} token - 认证token
 * @property {string} nickname - 昵称
 * @property {string} note_name - 备注名
 * @property {number} status - 账号状态
 * @property {string} avatar - 头像URL
 * @property {string} avatar_url - 头像链接
 * @property {number} avatar_status - 头像审核状态（0:待审核 1:审核成功 2:审核失败）
 * @property {number} age - 年龄
 * @property {number} sex - 性别（0=男 1=女）
 * @property {string} birthday - 生日
 * @property {string} constellation - 星座
 * @property {string} nation - 国家代码
 * @property {string} national_flag - 国旗图标
 * @property {string} national_name - 国家名
 * @property {string} city_name - 城市名
 * @property {string} education - 教育程度
 * @property {string} profession - 职业
 * @property {number} role - 角色（0=普通用户 1=主播 10=白名单）
 * @property {Object} online - 在线状态
 * @property {boolean} visitor_account - 是否游客账号
 * @property {Object} love - 恋爱信息
 * @property {Object} album - 相册
 * @property {Array} upload_video - 视频列表
 * @property {number} moment_count - 动态数
 * @property {Object} wealth_level - 财富等级
 * @property {Object} vip_info - VIP信息
 * @property {Object} audio_sign - 语音签名
 * @property {Array<string>} language - 语言选项
 * @property {Array} tags - 标签
 * @property {string} phone - 手机号
 * @property {number} created_at - 注册时间
 * @property {number} video_cost - 视频通话价格
 * @property {number} audio_cost - 语音通话价格
 * @property {string} video_url - 白名单视频
 * @property {boolean} union - 是否展示公会管理
 * @property {Object} rtc_server - RTC配置
 */

/**
 * 查询用户信息
 * @param {Object} params - 查询参数
 * @param {string} params.member_id - 要查询的用户的ID（必填）
 * @param {string} [params.scene] - 场景标识，可选值：read_profile（查看资料页）
 * @returns {Promise<Member>} 用户信息
 */
export function getUserInfo(params, config = {}) {
  return get('/members/v1/info', params, config)
}

/**
 * 会员资产类型
 * @property {number} coin - 金币数量
 * @property {number} bean - 豆数量
 * @property {string} bean_link - 豆充值链接
 * @property {string} exchange_link - 兑换链接
 * @property {string} private_call_1v1_card - 1v1连麦卡数量
 * @property {string} private_call_exp_card - 体验卡数量
 * @property {string} random_match_card - 匹配卡数量
 * @property {string} video_card_count - 视频卡数量（体验卡+连麦卡）
 * @property {string} intimacy_call_card - 专属卡id集合（用逗号分隔）
 * @property {string} message_card_count - 聊天卡数量
 * @property {number} bestie_coins - 密友币数量
 * @property {number} game_ticket_count - 游戏券数量
 */
export function getMemberAsset(params, config = {}) {
  return get('/member-asset/v1/asset', params, config)
}

/**
 * @typedef {Object} AnchorItem - 主播信息
 * @property {number} member_id - 主播用户 ID
 * @property {string} member_uid - 主播加密 UID
 * @property {string} nickname - 昵称
 * @property {string} avatar - 头像 URL
 * @property {number} age - 年龄
 * @property {string} nation - 国家代码
 * @property {string} nation_flag - 国旗图标 URL
 * @property {string} nation_name - 国家名称
 * @property {number} online - 在线状态（1-在线，0-离线）
 * @property {Object} online_status - 在线状态详情对象
 * @property {number} status - 主播状态
 * @property {number} price - 视频通话价格（金币/分钟）
 * @property {number} original_video_cost - 原始视频价格
 * @property {number} ticket_price - 门票价格
 * @property {number} free - 是否免费（1-免费，0-付费）
 * @property {string} video_type - 视频类型
 * @property {number} score - 评分
 * @property {number} sorter - 排序权重
 * @property {Object} anchor_level - 主播等级信息
 * @property {number} anchor_sexy - 主播性感度标签
 * @property {number} avatar_status - 头像审核状态
 * @property {string} system_language - 系统语言
 * @property {string} tag_type - 标签类型
 * @property {string} target_distance_str - 距离描述（如 "1.5km"）
 * @property {number} last_rec_timestamp - 最后推荐时间戳
 */

/**
 * @typedef {Object} RecommendUserListResponse - 推荐用户列表响应
 * @property {AnchorItem[]} member_list - 用户列表（主播列表）
 * @property {boolean} is_over - 是否已加载完所有数据
 * @property {Object} sayhi_config - 打招呼配置信息
 * @property {Object} level_info - 等级信息
 * @property {string} lado_rank_url - Lado 排名 URL
 * @property {Object} recommend_area - 推荐区域信息
 * @property {number} exposure_seconds - 曝光秒数
 */

/**
 * 获取推荐用户列表（主播列表）
 * @param {Object} params - 查询参数
 * @param {number} [params.page] - 页码
 * @param {number} [params.page_size] - 每页数量
 * @param {number} [params.tag_id] - 标签ID（用于筛选特定标签的用户，如 Pizza、Coffee 等）
 * @param {string} [params.country] - 国家代码（如 "US"、"GB"）
 * @param {string} [params.state] - 州/省名称
 * @returns {Promise<RecommendUserListResponse>} 返回推荐用户列表数据
 */
export function getRecommendUserList(params, config = {}) {
  return get('/live/v1/recommend/user_list', params, { hideLoading: true, ...config })
}

/**
 * 更新用户信息
 * @param {Object} data - 更新数据
 * @param {string} [data.nickname] - 昵称
 * @param {number} [data.age] - 年龄
 * @param {string} [data.birthday] - 生日
 * @param {string} [data.education] - 学历
 * @param {string} [data.profession] - 职业
 * @param {Array<string>} [notMustUpdate] - 不强制更新的字段列表
 * @returns {Promise<any>} 返回更新结果
 */
export function updateUserInfo(data, notMustUpdate = [], config = {}) {
  return post('/members/v1/update_info', data, { params: notMustUpdate.length > 0 ? { 'not_must_update[]': notMustUpdate } : {}, ...config })
}

/**
 * 上传图片（通用接口）
 * 支持上传头像、相册图片、房间封面等
 *
 * @param {Object} params - 上传参数
 * @param {File} params.picture - 图片文件（支持格式：.jpg, .jpeg, .png, .gif, .bmp, .webp, .heif）
 * @param {number} params.seat - 图片位置/排序
 *   - 0: 头像
 *   - >0: 相册图片（按顺序递增，最多9张）
 * @param {string} [params.upload_type='picture'] - 上传类型
 *   - 'picture': 相册图片（默认）
 *   - 'room_cover': 房间封面
 *   - 'avatar': 头像（当 seat=0 时）
 * @param {string} [params.category] - 分类标识（可选）
 *
 * @returns {Promise<string>} 返回图片哈希值/ID
 *
 * @example
 * // 上传头像
 * const formData = new FormData()
 * formData.append('picture', file)
 * formData.append('seat', 0)
 * formData.append('upload_type', 'avatar')
 * await uploadImage(formData)
 *
 * @example
 * // 上传相册图片
 * const formData = new FormData()
 * formData.append('picture', file)
 * formData.append('seat', 1)
 * formData.append('upload_type', 'picture')
 * await uploadImage(formData)
 *
 * @example
 * // 上传房间封面
 * const formData = new FormData()
 * formData.append('picture', file)
 * formData.append('seat', 0)
 * formData.append('upload_type', 'room_cover')
 * await uploadImage(formData)
 *
 * @throws {Error} 40000 - 超过上传限制（超过9张或今日上传次数超过30次）
 * @throws {Error} 50100 - 系统错误
 * @throws {Error} 501000 - 今日图片上传次数已达上限
 *
 * @note 审核规则：异步审核
 * - 普通用户：机审通过即可
 * - 主播：头像和超过2张的相册图片需要人审
 * - seat=0 时，审核通过会自动更新用户头像
 *
 * @note 限制：
 * - 最多上传 9 张相册图片
 * - 每日最多上传 30 次
 * - 主播 30 天内不能上传相同图片
 *
 * @note 存储路径：
 * - 相册图片：uploads/picture/path/{memberId}/{fileName}
 * - 房间封面：uploads/room_cover/path/{memberId}/{fileName}
 * - 头像：uploads/member_avatar/avatar/{memberId}/{fileName}
 */
export function uploadImage(data, config = {}) {
  const { file, seat, upload_type } = data
  const formData = new FormData()
  formData.append('picture', file)
  formData.append('seat', seat)
  formData.append('upload_type', upload_type)

  return upload('/members/v1/album/upload', formData, {
    hideError: true, // 关闭默认 toast，使用自定义错误处理
    ...config,
  }).catch((error) => {
    // 如果业务层传入了 errorCodeMap，优先使用业务层的映射（保留灵活性）
    if (config.errorCodeMap && config.errorCodeMap[error.code]) {
      const message = config.errorCodeMap[error.code]
      showToast(message)
      throw error
    }

    // 默认错误码映射（统一管理）
    const defaultErrorMessages = {
      40000: t('profile.uploadLimitExceeded'),
      50100: t('common.request.serverError'),
      501000: t('profile.uploadDailyLimitExceeded'),
    }

    const message = defaultErrorMessages[error.code] || error.message

    // 如果业务层设置了 hideError，不显示 toast（完全自定义）
    if (!config.hideError) {
      showToast(message)
    }

    throw error
  })
}

/**
 * 上传头像（便捷方法）
 * @param {File} file - 图片文件
 * @returns {Promise<string>} 返回图片哈希值/ID
 *
 * @example
 * const result = await uploadAvatar(file)
 */
export function uploadAvatar(file) {
  return uploadImage({
    file,
    seat: 0,
    upload_type: 'avatar',
  })
}

/**
 * @typedef {Object} MemberRelationBean - 用户关系信息
 * @property {number} type - 关系类型 (0:无关系, 1:关注, 2:被关注, 3:好友, 4:已拉黑, 5:被拉黑, 6:申请, 8:互相拉黑, 9:相互关注)
 * @property {number} RELATION_TYPE - 关注类型 (0:无关系, 1:关注, 2:被关注, 3:好友, 4:已拉黑, 5:被拉黑, 6:申请, 8:互相拉黑, 9:相互关注)
 * @property {number} create_time - 创建时间（时间戳）
 */

/**
 * 查询用户关系
 * @param {Object} params
 * @param {string} params.target_id - 目标用户ID，加密 ID
 * @returns {Promise<MemberRelationBean>}
 */
export function getRelationWithId(params, config = {}) {
  return get('/v1/relations/query', params, config)
}

/**
 * @typedef {Object} AnchorStatusBean - 主播状态信息
 * @property {string} member_id - 主播ID (明文ID)
 * @property {string} member_uid - 主播加密ID
 * @property {number} price - 视频通话价格 (金币/分钟)
 * @property {number} online_status - 主播在线状态 (0:离线, 1:在线, 2:忙碌)
 * @property {number} free - 是否可用体验卡 (0:不可用, 1:可用)
 * @property {number} ticket_price - 情侣模式门票价格
 * @property {string} video_type - 视频类型 (normal:自由, lover:情侣, 其他值默认不展示)
 * @property {number} original_video_cost - 原始视频通话价格
 * @property {number} countdown - 忙线倒计时
 * @property {Object} party_info - 多人房对象
 * @property {number} show_live - 秀场标签
 * @property {string} item_type - 项目类型
 */

/**
 * 获取主播状态
 * @param {Object} data
 * @param {Array<string>} data.target_ids - 目标用户ID列表，加密 ID
 * @param {string} data.scene - 场景
 * @param {string} data.source - 来源
 * @param {number} data.action - 动作
 * @returns {Promise<AnchorStatusBean[]>}
 */
export function getAnchorStatus(data, config = {}) {
  // 将数据转换为 FormData 格式（后端使用 @FormUrlEncoded）
  const formData = new URLSearchParams()

  // target_ids 是数组，需要转换为JSON字符串或逐个添加
  if (data.target_ids && Array.isArray(data.target_ids)) {
    data.target_ids.forEach(id => {
      formData.append('target_ids', id)
    })
  }

  if (data.scene) formData.append('scene', data.scene)
  if (data.source) formData.append('source', data.source)
  if (data.action !== undefined) formData.append('action', data.action)
  if (data.show_room_host_id) formData.append('show_room_host_id', data.show_room_host_id)

  return post('/live/v1/love_room/anchor_status', formData, {
    ...config,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  })
}
Object.assign(getAnchorStatus, {
  SCENE: {
    PERSONAL_PAGE: 'personal_page', // 用户详情页
    VIDEO_LIST: 'video_list', // 主播列表页（webapp 首页）
    MESSAGE_PAGE: 'message_page', // 私聊页面
  },
  ACTION: {
    DEFAULT: 0, // 默认查询+上报曝光
    NO_REPORT: 1, // 上报关闭
    ONLY_CHECK: 2, // 只查询
  },
  SOURCE: {
    ANCHOR_LIST: 'anchor_list', // 主播列表页（webapp首页）
    PERSONAL_PROFILE: 'personal_profile', // 用户详情页
    MESSAGE_PAGE: 'msg_page', // 私聊页面
  }
})

/**
 * 关注用户
 * @param {Object} data
 * @param {string} data.target_id - 目标用户ID
 * @returns {Promise<any>}
 */
export function followUser(data) {
  return post('/v1/relations/follow', data)
}

/**
 * 取消关注用户
 * @param {Object} data
 * @param {string} data.target_id - 目标用户ID
 * @returns {Promise<any>}
 */
export function unfollowUser(data) {
  return post('/v1/relations/unfollow', data)
}

/**
 * 拉黑或取消拉黑用户
 * @param {Object} params
 * @param {string} params.member_id - 目标用户ID
 * @param {string} params.action - 操作类型: 'block'=拉黑, 'cancel'=取消拉黑
 * @returns {Promise<string>}
 */
export function blockUser(params) {
  return post('/v1/relations/block', null, {
    params,
  })
}
blockUser.ACTION = {
  BLOCK: 'block',
  CANCEL: 'cancel',
}

/**
 * @typedef {Object} ReportCategory - 举报分类
 * @property {string} title - 分类标题
 * @property {string} cid - 分类ID
 * @property {Array<ReportCategory>} [subs] - 子分类列表
 */

/**
 * 获取举报分类列表
 * @param {Object} params
 * @param {string} params.targetId - 目标用户ID
 * @param {number} [params.source=0] - 来源类型（0:正常 1:不区分红娘和普通用户）
 * @returns {Promise<Array<ReportCategory>>}
 */
export function getReportCategories(params) {
  return get('/v1/reports/categories', params)
}

/**
 * 提交举报
 * @param {Object} params
 * @param {string} params.target_id - 目标用户ID
 * @param {string} [params['report[reason]']] - 举报原因
 * @param {string} [params['report[report_type]']] - 举报类型（1:举报用户）
 * @param {string} [params['report[report_money]']] - 金额
 * @param {string} [params['report[report_category]']] - 举报分类ID
 * @param {string} [params['report[report_sub_category]']] - 举报子分类ID
 * @param {string} [params['report[big_account_id]']] - 大号ID
 * @param {string} [params['report[is_block]']] - 是否拉黑（0:不拉黑 1:拉黑）
 * @param {string} [params['report[report_source]']] - 举报来源（4:个人页）
 * @param {string} [params['report[report_source_id]']] - 举报来源ID
 * @param {string} [params['report[report_email]']] - 举报邮箱
 * @returns {Promise<any>}
 */
export function reportUser(params) {
  return post('/v1/reports/member', null, {
    params,
  })
}

/**
 * 获取用户个人动态列表
 * @param {Object} params
 * @param {string} params.target_id - 目标用户ID
 * @param {string} [params.last_moment_id] - 最后一个动态ID（用于分页）
 * @param {number} [params.page_size=10] - 每页数量
 * @returns {Promise<any>}
 */
export function getUserMoments(params, config = {}) {
  return get('/v1/member_moment/personal', params, config)
}

/**
 * @typedef {Object} FollowMember - 关注列表成员信息
 * @property {number} member_id - 用户ID
 * @property {string} nickname - 昵称
 * @property {string} avatar - 头像URL
 * @property {number} sex - 性别（0=男 1=女）
 * @property {number} follow_type - 关注类型（1=我关注的, 2=关注我的, 3=互相关注）
 * @property {boolean} is_online - 是否在线
 * @property {Object} online - 在线状态详情
 * @property {number} online.last_online_time - 最后在线时间（Unix时间戳，秒）
 * @property {Object} wealth_info - 财富信息
 * @property {Object} consume_info - 消费信息
 */

/**
 * @typedef {Object} FollowListResponse - 关注列表响应
 * @property {FollowMember[]} member_list - 关注用户列表
 */

/**
 * 获取关注列表
 * @param {Object} params - 查询参数
 * @param {number} params.page - 页码（从1开始）
 * @param {number} params.page_size - 每页数量
 * @param {Object} [config] - 请求配置
 * @returns {Promise<FollowListResponse>} 关注列表数据
 */
export function getFollowList(params, config) {
  return get('/v1/relations/follow_list', params, config)
}

/**
 * @typedef {Object} RiskStatus - 用户风控状态
 * @property {number} banned_num - 封禁相关次数/计数（如空播次数等）
 * @property {number} unbanned_time - 解封时间（Unix 时间戳，秒）
 * @property {number} banned_hour - 封禁时长（小时）；≤0 表示永久封禁
 * @property {string} title - 弹窗标题（已翻译）
 * @property {string} body - 弹窗正文（已翻译，含封禁原因/时长等）
 * @property {string} extra - 额外提示（如时间提醒，已翻译）
 * @property {string} button_content - 按钮文案（如「退出」等，已翻译）
 */

/**
 * 获取当前用户的封禁/风控状态
 * @param {Object} [config] - 请求配置
 * @returns {Promise<RiskStatus>} 用户风控状态信息
 */
export function getRiskStatus(config = {}) {
  return get('/live/v1/risk_status', {}, {
    hideLoading: true,
    hideError: true,
    ...config,
  })
}
